import { useRef, useState } from "react";

import { Link } from "react-router-dom";

import {
  DropdownHeader,
  DropdownItem,
  DropdownMenu,
  type DropdownProps,
  Dropdown as SemanticDropdown,
  type StrictDropdownItemProps,
} from "semantic-ui-react";
import { styled } from "styled-components";

const StyledAccessibleDropdown = styled(SemanticDropdown)`
  .header {
    margin: 0 !important;
    pointer-events: none !important;
  }
  > .text {
    font-weight: normal !important;
  }
`;

type AccessibleDropdownOption = StrictDropdownItemProps & {
  value: NonNullable<StrictDropdownItemProps["value"]>;
} & { key?: string | number; as?: "a"; href?: string; target?: string };

type AccessibleDropdownOptionItem = Omit<AccessibleDropdownOption, "as"> & {
  as?: AccessibleDropdownOption["as"] | typeof Link;
  to?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
};

type AccessibleDropdownOptionValue = AccessibleDropdownOptionItem["value"];

const isAccessibleDropdownOption = (
  option: AccessibleDropdownOptionItem | null | undefined,
): option is AccessibleDropdownOptionItem => Boolean(option);

const OptionsDropdown = (props: Omit<DropdownProps, "children">) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = useState(props.open ?? false);
  const [internalSelection, setInternalSelection] =
    useState<AccessibleDropdownOptionValue | null>(
      props.value ?? props.defaultValue ?? null,
    );

  const options = (
    (props.options || []) as (AccessibleDropdownOptionItem | null | undefined)[]
  )
    .filter(isAccessibleDropdownOption)
    .map((option) => ({
      ...option,
      key: option.key || option.value,
      value: option.value || option.key,
    }));
  const isOpen = props.open !== undefined ? props.open : internalOpen;

  return (
    <StyledAccessibleDropdown
      ref={dropdownRef}
      {...props}
      tabIndex={props.trigger ? -1 : 0}
      openOnFocus={props.openOnFocus ?? false}
      inline={props.inline ?? false}
      multiple={props.multiple ?? false}
      icon={props.icon}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
        if (props.clearable && (e.key === "Backspace" || e.key === "Delete")) {
          e.preventDefault();
          setInternalSelection(null);
          props.onChange?.(e, { ...props, value: null });
        }

        if (isOpen && internalSelection) {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            const option = options.find(
              (opt) => opt.value === internalSelection,
            );
            if (option?.onClick) {
              option.onClick(e as unknown as React.MouseEvent<HTMLDivElement>, {
                ...props,
                value: option.value,
              });
            }
            if (option?.href) {
              window.open(
                option.href,
                option.target || "_self",
                "noopener noreferrer",
              );
            }
            setInternalSelection(null);
          }
        }
        props.onKeyDown?.(e);
      }}
      onChange={(e: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
        setInternalSelection(data.value as AccessibleDropdownOptionValue);
        props.onChange?.(e, data);
      }}
      options={options}
      onOpen={(e: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
        setInternalOpen(true);
        props.onOpen?.(e, data);
      }}
      onClose={(e: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
        setInternalOpen(false);
        props.onClose?.(e, data);
      }}
    />
  );
};

const Dropdown = (props: DropdownProps) => {
  if (props.children) {
    return <SemanticDropdown {...props} />;
  }

  return <OptionsDropdown {...props} />;
};

Dropdown.Menu = SemanticDropdown.Menu;
Dropdown.Item = SemanticDropdown.Item;
Dropdown.Divider = SemanticDropdown.Divider;
Dropdown.Header = SemanticDropdown.Header;

export { Dropdown, DropdownHeader, DropdownItem, DropdownMenu };
